import FileText from 'lucide-react-native/dist/esm/icons/file-text.js';
import List from 'lucide-react-native/dist/esm/icons/list.js';
import LogOut from 'lucide-react-native/dist/esm/icons/log-out.js';
import Shield from 'lucide-react-native/dist/esm/icons/shield.js';
import Star from 'lucide-react-native/dist/esm/icons/star.js';
import UserIcon from 'lucide-react-native/dist/esm/icons/user.js';
import X from 'lucide-react-native/dist/esm/icons/x.js';
import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Modal,
  Pressable,
  Platform,
  Animated,
  ScrollView,
  Alert,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { ConfirmationDialog } from './ConfirmationDialog';
import { Sliders } from './icons';
import { SyncStatus } from './SyncStatus';
import { featureFlags } from '../config/featureFlags';
import { useAuth } from '../contexts/AuthContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { roleService } from '../services/auth/roleService';
import { colors } from '../theme/colors';
import { getVersionDisplay } from '../utils/appVersion';

interface NavigationDrawerProps {
  isVisible: boolean;
  onClose: () => void;
  navigate?: (path: string) => void;
  currentPath?: string; // Add current path to determine active state
}

export const NavigationDrawer: React.FC<NavigationDrawerProps> = ({
  isVisible,
  onClose,
  navigate,
  currentPath,
}) => {
  const insets = useSafeAreaInsets();
  const { user, logout } = useAuth();
  const { isImpersonating, session, endImpersonation } = useImpersonation();
  const [showLogoutConfirm, setShowLogoutConfirm] = React.useState(false);

  // Check if user can access role management (IT Admin or dev access)
  const canAccessRoleManagement = roleService.canAccessRoleManagement(user);
  // Check if user can access prompt management (IT Admin only + feature flag)
  const canAccessPromptManagement =
    featureFlags.promptManagement &&
    roleService.canAccessPromptManagement(user);
  // Check if user can access feedback view (IT Admin only)
  const canAccessFeedbackView = roleService.canAccessFeedbackView(user);

  const [modalVisible, setModalVisible] = React.useState(false);
  const slideAnim = React.useRef(new Animated.Value(280)).current; // Start off-screen (drawer width)
  const backdropAnim = React.useRef(new Animated.Value(0)).current;
  const pendingNavigationPathRef = React.useRef<string | null>(null);

  const flushPendingNavigation = React.useCallback(() => {
    const path = pendingNavigationPathRef.current;
    if (!path || !navigate) {
      return;
    }
    pendingNavigationPathRef.current = null;
    void navigate(path);
  }, [navigate]);

  /** Close drawer first; on native defer route change until Modal unmounts (#997 / #1116). */
  const requestNavigate = React.useCallback(
    (path: string) => {
      onClose();
      if (!navigate) {
        return;
      }
      if (Platform.OS === 'web') {
        navigate(path);
        return;
      }
      pendingNavigationPathRef.current = path;
    },
    [navigate, onClose],
  );

  React.useEffect(() => {
    if (isVisible) {
      pendingNavigationPathRef.current = null;
      // Show modal first, then animate in
      setModalVisible(true);
      Animated.parallel([
        Animated.timing(slideAnim, {
          toValue: 0,
          duration: 300,
          useNativeDriver: false, // Disable native driver for cross-platform consistency
        }),
        Animated.timing(backdropAnim, {
          toValue: 1,
          duration: 300,
          useNativeDriver: false,
        }),
      ]).start();
    } else if (modalVisible) {
      // Animate out first, then hide modal
      Animated.parallel([
        Animated.timing(slideAnim, {
          toValue: 280,
          duration: 250,
          useNativeDriver: false,
        }),
        Animated.timing(backdropAnim, {
          toValue: 0,
          duration: 250,
          useNativeDriver: false,
        }),
      ]).start(() => {
        setModalVisible(false);
        if (Platform.OS !== 'web') {
          flushPendingNavigation();
        }
      });
    }
  }, [
    isVisible,
    slideAnim,
    backdropAnim,
    modalVisible,
    flushPendingNavigation,
  ]);

  const handleHomePress = () => {
    requestNavigate('/request');
  };

  const handleRoleManagementPress = () => {
    requestNavigate('/users');
  };

  const handlePromptManagementPress = () => {
    requestNavigate('/prompt-management');
  };

  const handleFeedbackViewPress = () => {
    requestNavigate('/feedback-view');
  };

  const handleProfilePress = () => {
    requestNavigate('/profile');
  };

  const handlePreferencesPress = () => {
    requestNavigate('/preferences');
  };

  const handleTermsConditionsPress = () => {
    requestNavigate('/terms-conditions');
  };

  const handleBackdropPress = () => {
    onClose();
  };

  const handleConfirmLogout = async () => {
    try {
      await endImpersonation();
    } catch {
      // Continue with logout even if end-impersonation fails
    }
    await logout();
    onClose();
  };

  const handleLogout = () => {
    if (isImpersonating) {
      if (Platform.OS !== 'web') {
        // Native: use system Alert to avoid nested Modal issues
        Alert.alert(
          'Logout',
          `You are currently impersonating ${
            session?.target_user?.name || 'another user'
          }. Logging out will end the impersonation session and log out your account.`,
          [
            { text: 'Cancel', style: 'cancel' },
            {
              text: 'Logout',
              style: 'destructive',
              onPress: handleConfirmLogout,
            },
          ],
        );
      } else {
        setShowLogoutConfirm(true);
      }
      return;
    }
    logout();
    onClose();
  };

  return (
    <>
      <Modal
        visible={modalVisible}
        transparent={true}
        animationType="none"
        onRequestClose={onClose}
      >
        <View style={styles.container}>
          <Animated.View
            style={[
              styles.backdrop,
              {
                opacity: backdropAnim,
              },
            ]}
          >
            <Pressable
              style={styles.backdropPress}
              onPress={handleBackdropPress}
            />
          </Animated.View>

          <Animated.View
            style={[
              styles.drawer,
              {
                paddingTop: insets.top + 20,
                transform: [{ translateX: slideAnim }],
              },
            ]}
          >
            <View style={styles.header}>
              <View style={styles.userInfoSection}>
                <Text style={styles.userName}>{user?.name}</Text>
                <Text style={styles.userEmail}>{user?.email}</Text>
                <View style={styles.syncStatusContainer}>
                  <SyncStatus
                    showDetails={false}
                    hideButtons={true}
                    style={styles.syncStatusInline}
                  />
                </View>
              </View>
              <TouchableOpacity style={styles.closeButton} onPress={onClose}>
                <X size={24} color={colors.textSecondary} />
              </TouchableOpacity>
            </View>

            <ScrollView
              style={styles.navItems}
              contentContainerStyle={styles.navItemsContent}
              showsVerticalScrollIndicator={true}
            >
              {canAccessRoleManagement && (
                <TouchableOpacity
                  style={[
                    styles.navItem,
                    (currentPath === '/users' ||
                      currentPath === '/role-management') &&
                      styles.navItemActive,
                  ]}
                  onPress={handleRoleManagementPress}
                >
                  <Shield
                    size={20}
                    color={
                      currentPath === '/users' ||
                      currentPath === '/role-management'
                        ? colors.primary
                        : colors.textSecondary
                    }
                  />
                  <Text
                    style={
                      currentPath === '/users' ||
                      currentPath === '/role-management'
                        ? styles.navItemTextActive
                        : styles.navItemText
                    }
                  >
                    Users
                  </Text>
                </TouchableOpacity>
              )}

              {canAccessPromptManagement && Platform.OS === 'web' && (
                <TouchableOpacity
                  style={[
                    styles.navItem,
                    currentPath === '/prompt-management' &&
                      styles.navItemActive,
                  ]}
                  onPress={handlePromptManagementPress}
                >
                  <FileText
                    size={20}
                    color={
                      currentPath === '/prompt-management'
                        ? colors.primary
                        : colors.textSecondary
                    }
                  />
                  <Text
                    style={
                      currentPath === '/prompt-management'
                        ? styles.navItemTextActive
                        : styles.navItemText
                    }
                  >
                    Prompt Management
                  </Text>
                </TouchableOpacity>
              )}

              {/* Feedback hidden from nav — canAccessFeedbackView kept for future re-enable */}
              {false && canAccessFeedbackView && (
                <TouchableOpacity
                  style={[
                    styles.navItem,
                    currentPath === '/feedback-view' && styles.navItemActive,
                  ]}
                  onPress={handleFeedbackViewPress}
                >
                  <Star
                    size={20}
                    color={
                      currentPath === '/feedback-view'
                        ? colors.primary
                        : colors.textSecondary
                    }
                  />
                  <Text
                    style={
                      currentPath === '/feedback-view'
                        ? styles.navItemTextActive
                        : styles.navItemText
                    }
                  >
                    Feedback View
                  </Text>
                </TouchableOpacity>
              )}

              <TouchableOpacity
                style={[
                  styles.navItem,
                  currentPath === '/profile' && styles.navItemActive,
                ]}
                onPress={handleProfilePress}
              >
                <UserIcon
                  size={20}
                  color={
                    currentPath === '/profile'
                      ? colors.primary
                      : colors.textSecondary
                  }
                />
                <Text
                  style={
                    currentPath === '/profile'
                      ? styles.navItemTextActive
                      : styles.navItemText
                  }
                >
                  Profile
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[
                  styles.navItem,
                  currentPath === '/preferences' && styles.navItemActive,
                ]}
                onPress={handlePreferencesPress}
              >
                <Sliders
                  size={20}
                  color={
                    currentPath === '/preferences'
                      ? colors.primary
                      : colors.textSecondary
                  }
                />
                <Text
                  style={
                    currentPath === '/preferences'
                      ? styles.navItemTextActive
                      : styles.navItemText
                  }
                >
                  Preferences
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[
                  styles.navItem,
                  currentPath === '/terms-conditions' && styles.navItemActive,
                ]}
                onPress={handleTermsConditionsPress}
              >
                <FileText
                  size={20}
                  color={
                    currentPath === '/terms-conditions'
                      ? colors.primary
                      : colors.textSecondary
                  }
                />
                <Text
                  style={
                    currentPath === '/terms-conditions'
                      ? styles.navItemTextActive
                      : styles.navItemText
                  }
                >
                  Terms & Conditions
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[
                  styles.navItem,
                  currentPath === '/request' && styles.navItemActive,
                ]}
                onPress={handleHomePress}
              >
                <List
                  size={20}
                  color={
                    currentPath === '/request'
                      ? colors.primary
                      : colors.textSecondary
                  }
                  style={{ marginLeft: 0 }}
                />
                <Text
                  style={
                    currentPath === '/request'
                      ? styles.navItemTextActive
                      : styles.navItemText
                  }
                >
                  Requests
                </Text>
              </TouchableOpacity>

              <View style={styles.divider} />

              <TouchableOpacity
                style={[styles.navItem, { marginTop: 0 }]}
                onPress={handleLogout}
              >
                <LogOut
                  size={20}
                  color={colors.error}
                  style={{ marginLeft: 0 }}
                />
                <Text style={[styles.navItemText, { color: colors.error }]}>
                  Logout
                </Text>
              </TouchableOpacity>

              <View style={styles.versionContainer}>
                <Text style={styles.versionText}>{getVersionDisplay()}</Text>
              </View>
            </ScrollView>
          </Animated.View>
        </View>
      </Modal>

      <ConfirmationDialog
        visible={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        onConfirm={handleConfirmLogout}
        title="Logout"
        message={`You are currently impersonating ${
          session?.target_user?.name || 'another user'
        }. Logging out will end the impersonation session and log out your account.`}
        confirmText="Logout"
        confirmButtonStyle="danger"
      />
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    position: 'relative',
  },
  backdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  backdropPress: {
    flex: 1,
  },
  drawer: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    width: 280,
    backgroundColor: colors.backgroundPrimary,
    paddingHorizontal: 20,
    paddingBottom: 20,
    ...Platform.select({
      web: {
        boxShadow: '-2px 0 8px rgba(0, 0, 0, 0.1)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: {
          width: -2,
          height: 0,
        },
        shadowOpacity: 0.1,
        shadowRadius: 8,
        elevation: 5,
      },
    }),
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderLight,
    marginBottom: 20,
  },
  userInfoSection: {
    flex: 1,
    marginRight: 16,
    paddingHorizontal: 12,
  },
  userName: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.textPrimary,
    marginBottom: 4,
  },
  userEmail: {
    fontSize: 14,
    color: colors.textSecondary,
    marginBottom: 8,
  },
  syncStatusContainer: {
    marginTop: 4,
  },
  syncStatusInline: {
    backgroundColor: 'transparent',
    padding: 0,
    borderWidth: 0,
    borderColor: 'transparent',
  },
  closeButton: {
    padding: 4,
  },
  navItems: {
    flex: 1,
  },
  navItemsContent: {
    gap: 8,
    paddingBottom: 20,
  },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderRadius: 8,
    gap: 12,
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  navItemActive: {
    backgroundColor: '#fff5f1',
  },
  navItemText: {
    fontSize: 16,
    color: colors.textSecondary,
    fontWeight: '500',
  },
  navItemTextActive: {
    fontSize: 16,
    color: colors.primary,
    fontWeight: '500',
  },
  divider: {
    height: 1,
    backgroundColor: colors.borderLight,
    marginVertical: 8,
    marginHorizontal: 12,
  },
  versionContainer: {
    paddingVertical: 16,
    paddingHorizontal: 12,
    alignItems: 'center',
    marginTop: 8,
  },
  versionText: {
    fontSize: 12,
    color: colors.textMuted,
  },
});
